import type {
  AssetScreenshots,
  ReportHeatmapAsset,
} from 'pages/reporting/reports/rug/hooks/useAssetScreenshots'
import type { PdfHeatmapWorkerResult } from 'pages/reporting/reports/rug/workers/pdfHeatmap.worker'
import PdfHeatmapWorker from 'pages/reporting/reports/rug/workers/pdfHeatmap.worker?worker'

async function encodeImages(urls: string[], signal: AbortSignal) {
  signal.throwIfAborted()
  const worker = new PdfHeatmapWorker()
  let abort: (() => void) | undefined
  try {
    return await new Promise<{ source: string; blob: Blob }[]>(
      (resolve, reject) => {
        abort = () => reject(signal.reason)
        worker.onmessage = ({ data }: MessageEvent<PdfHeatmapWorkerResult>) => {
          if ('error' in data) reject(new Error(data.error))
          else resolve(data.images)
        }
        const fail = () => reject(new Error('Failed to prepare PDF heatmaps'))
        worker.onerror = fail
        worker.onmessageerror = fail
        signal.addEventListener('abort', abort, { once: true })
        worker.postMessage(urls)
      }
    )
  } finally {
    if (abort) signal.removeEventListener('abort', abort)
    worker.onmessage = null
    worker.onerror = null
    worker.onmessageerror = null
    worker.terminate()
  }
}

export interface PreparedPdfHeatmaps {
  screenshots: AssetScreenshots | undefined
  dispose: () => void
}

/** Creates generation-scoped PDF copies without changing report preview images. */
export async function preparePdfHeatmaps(
  screenshots: AssetScreenshots | undefined,
  signal: AbortSignal
): Promise<PreparedPdfHeatmaps> {
  signal.throwIfAborted()
  const sources = new Map<string, string>()
  const dispose = () => {
    sources.forEach((url) => URL.revokeObjectURL(url))
    sources.clear()
  }
  const assets = [
    ...(screenshots?.fullComponentHeatmap ?? []),
    ...(screenshots?.sectionHeatmap ?? []),
  ]
  const urls = [
    ...new Set(
      assets
        .flatMap((asset) => [
          asset.signedUrl,
          asset.heatmapSignedUrl,
          asset.legendSignedUrl,
        ])
        .filter((url): url is string => Boolean(url))
    ),
  ]
  if (!screenshots || urls.length === 0) return { screenshots, dispose }

  const replaceSource = (url: string | undefined) =>
    url ? sources.get(url) : url
  const prepareAssets = (assets: ReportHeatmapAsset[] | undefined) =>
    assets?.map((asset) => ({
      ...asset,
      signedUrl: replaceSource(asset.signedUrl),
      heatmapSignedUrl: replaceSource(asset.heatmapSignedUrl),
      legendSignedUrl: replaceSource(asset.legendSignedUrl),
    }))
  try {
    const images = await encodeImages(urls, signal)
    signal.throwIfAborted()
    for (const { source, blob } of images) {
      sources.set(source, URL.createObjectURL(blob))
    }
    return {
      screenshots: {
        ...screenshots,
        fullComponentHeatmap: prepareAssets(screenshots.fullComponentHeatmap),
        sectionHeatmap: prepareAssets(screenshots.sectionHeatmap),
      },
      dispose,
    }
  } catch (error) {
    dispose()
    throw error
  }
}
